import * as React from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  Divider,
  FormControl,
  InputLabel,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LanguageIcon from "@mui/icons-material/Language";
import InsertInvitationOutlinedIcon from "@mui/icons-material/InsertInvitationOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import {
  colors,
  typography,
  spacing,
} from "../../../../../../../assets/styles/tokens";
import { SharedTaskProps } from "../CreateTrendModules/CreateTrend";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";

interface Task {
  taskName: string;
  category: string;
  target: string;
  website: string;
  dueDate: Dayjs | null;
  assignee: string;
  priority: string;
}

interface AddTasksProps {
  sharedProps?: SharedTaskProps;
  onBack: () => void;
  onSave: () => void;
}

const emptyTask = (): Task => ({
  taskName: "",
  category: "",
  target: "",
  website: "",
  dueDate: null,
  assignee: "",
  priority: "",
});

export default function AddTasks({
  sharedProps,
  onBack,
  onSave,
}: AddTasksProps) {
  const [tasks, setTasks] = React.useState<Task[]>([emptyTask()]);
  const [pickerOpen, setPickerOpen] = React.useState<number | null>(null);

  const hasShared =
    sharedProps &&
    (sharedProps.assignee ||
      sharedProps.dueDate ||
      sharedProps.priority ||
      sharedProps.website);

  const updateTask = (
    index: number,
    field: keyof Task,
    value: string | Dayjs | null,
  ) => {
    setTasks((prev) =>
      prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)),
    );
  };

  const addAnother = () => setTasks((prev) => [...prev, emptyTask()]);

  const deleteTask = (index: number) => {
    setTasks((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: spacing.Spacing.xl.$value,
            px: spacing.Spacing.xl.$value,
          }}
        >
          <IconButton onClick={onBack} size="small">
            <ArrowBackIosIcon
              fontSize="small"
              sx={{ color: colors.GrayDarkMode[700].$value }}
            />
          </IconButton>
          <Typography
            sx={{
              fontWeight: typography.Font_weight.Medium.$value,
              fontSize: typography.Size.Text.xl.$value,
              color: colors.GrayLightMode[900].$value,
            }}
          >
            Add Tasks
          </Typography>
          <Button
            onClick={onSave}
            sx={{
              color: colors.Brand[600].$value,
              textTransform: "none",
              fontWeight: typography.Font_weight.Bold.$value,
              padding: spacing.Spacing.none.$value,
            }}
          >
            Save
          </Button>
        </Box>
        <Divider />

        {/* Scrollable body */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: spacing.Spacing.xl.$value,
            pt: spacing.Spacing.md.$value,
          }}
        >
          {/* ── Shared Defaults Banner ── */}
          {hasShared && (
            <Box
              sx={{
                border: `1px solid ${colors.GrayLightMode[200].$value}`,
                borderRadius: spacing.Radius.md.$value,
                p: spacing.Spacing.md.$value,
                mb: spacing.Spacing.xl.$value,
                backgroundColor: colors.Base.White.$value,
              }}
            >
              {/* Banner header */}
              <Typography
                sx={{
                  fontSize: typography.Size.Text.md.$value,
                  fontWeight: typography.Font_weight.Medium.$value,
                  color: colors.GrayDarkMode[700].$value,
                  marginInline: spacing.Spacing.md.$value,
                  marginBottom: spacing.Spacing.md.$value,
                }}
              >
                Shared Defaults
              </Typography>
              <Divider></Divider>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  marginTop: spacing.Spacing.md.$value,
                  marginInline: spacing.Spacing.md.$value,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: spacing.Spacing.md.$value,
                  }}
                >
                  {/* Assignee row */}
                  {sharedProps!.assignee && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.Spacing.xs.$value,
                      }}
                    >
                      <AccountCircleIcon
                        fontSize="medium"
                        sx={{ color: colors.GrayDarkMode[700].$value }}
                      />
                      <Typography
                        sx={{
                          fontSize: typography.Size.Text.xs.$value,
                          color: colors.GrayDarkMode[700].$value,
                          fontWeight: typography.Font_weight.Regular.$value,
                        }}
                      >
                        {sharedProps!.assignee}
                      </Typography>
                    </Box>
                  )}

                  {/* Due Date row */}
                  {sharedProps!.dueDate && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.Spacing.xs.$value,
                      }}
                    >
                      <InsertInvitationOutlinedIcon
                        fontSize="medium"
                        sx={{
                          color: colors.GrayDarkMode[700].$value,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: typography.Size.Text.xs.$value,
                          color: colors.GrayDarkMode[700].$value,
                          fontWeight: typography.Font_weight.Regular.$value,
                        }}
                      >
                        {sharedProps!.dueDate.format("DD/MM/YYYY")}
                      </Typography>
                    </Box>
                  )}

                  {/* Priority row */}
                  {sharedProps!.priority &&
                    (() => {
                      const priorityIconMap: Record<string, React.ReactNode> = {
                        high: (
                          <ExpandLessIcon
                            fontSize="medium"
                            sx={{ color: colors.Error[500].$value }}
                          />
                        ),
                        medium: (
                          <DragHandleIcon
                            fontSize="medium"
                            sx={{ color: colors.Warning[500].$value }}
                          />
                        ),
                        low: (
                          <ExpandMoreIcon
                            fontSize="medium"
                            sx={{ color: colors.Blue[500].$value }}
                          />
                        ),
                        highest: (
                          <KeyboardDoubleArrowUpIcon
                            fontSize="medium"
                            sx={{ color: colors.Error[500].$value }}
                          />
                        ),
                        lowest: (
                          <KeyboardDoubleArrowDownIcon
                            fontSize="medium"
                            sx={{ color: colors.Blue[500].$value }}
                          />
                        ),
                      };
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: spacing.Spacing.xs.$value,
                          }}
                        >
                          {priorityIconMap[sharedProps!.priority] ?? null}
                          <Typography
                            sx={{
                              fontSize: typography.Size.Text.xs.$value,
                              color: colors.GrayDarkMode[700].$value,
                              textTransform: "capitalize",
                            }}
                          >
                            {sharedProps!.priority}
                          </Typography>
                        </Box>
                      );
                    })()}

                  {/* Website row */}
                  {sharedProps!.website && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.Spacing.xs.$value,
                      }}
                    >
                      <LanguageIcon
                        fontSize="medium"
                        sx={{ color: colors.GrayLightMode[700].$value }}
                      />
                      <Typography
                        sx={{
                          fontSize: typography.Size.Text.xs.$value,
                          color: colors.GrayLightMode[700].$value,
                        }}
                      >
                        {sharedProps!.website}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {/* Edit → go back to shared step */}
                <IconButton
                  size="small"
                  onClick={onBack}
                  sx={{ p: spacing.Spacing.lg.$value }}
                >
                  <EditIcon
                    sx={{
                      fontSize: typography.Size.Text.xxl.$value,
                      color: colors.GrayDarkMode[700].$value,
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* ── Task Cards ── */}
          {tasks.map((task, index) => (
            <Box
              key={index}
              sx={{
                border: `1px solid ${colors.GrayLightMode[200].$value}`,
                borderRadius: spacing.Radius.md.$value,
                p: spacing.Spacing.md.$value,
                mb: spacing.Spacing.md.$value,
                backgroundColor: colors.Base.White.$value,
              }}
            >
              {/* Task header with close button */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.xs.$value,
                    fontWeight: typography.Font_weight.Medium.$value,
                    color: colors.GrayLightMode[500].$value,
                  }}
                >
                  Task #{index + 1}
                </Typography>

                {index > 0 && (
                  <IconButton size="small" onClick={() => deleteTask(index)}>
                    <CloseIcon
                      fontSize="medium"
                      sx={{ color: colors.Error[600].$value }}
                    />
                  </IconButton>
                )}
              </Box>

              {/* Task Name */}
              <TextField
                fullWidth
                placeholder="Task Name"
                value={task.taskName}
                onChange={(e) => updateTask(index, "taskName", e.target.value)}
                variant="standard"
                sx={{
                  mb: spacing.Spacing.xl.$value,
                  "& .MuiInput-underline:before": {
                    borderBottomColor: colors.GrayDarkMode[300].$value,
                  },
                  "& .MuiInput-underline:hover:before": {
                    borderBottomColor: colors.Brand[600].$value,
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: colors.Brand[600].$value,
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: colors.GrayDarkMode[400].$value,
                    opacity: 1,
                  },
                }}
                color="secondary"
              />

              {/* Category */}
              <TextField
                fullWidth
                placeholder="e.g. Deal, Design, Research"
                label="Category"
                value={task.category}
                onChange={(e) => updateTask(index, "category", e.target.value)}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                variant="outlined"
                size="small"
                sx={{
                  mb: spacing.Spacing["3xl"].$value,
                  "& .MuiOutlinedInput-root": {
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.Brand[600].$value,
                  },
                }}
              />

              {/* Target */}
              <TextField
                fullWidth
                placeholder="Specify your target for this item"
                label="Target"
                value={task.target}
                onChange={(e) => updateTask(index, "target", e.target.value)}
                variant="outlined"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.Brand[600].$value,
                  },
                }}
                size="small"
                multiline
              />

              {/* Website — only shown if NOT already set via shared props */}
              {!sharedProps?.website && (
                <FormControl
                  fullWidth
                  sx={{
                    mt: spacing.Spacing["3xl"].$value,
                    mb: spacing.Spacing.xs.$value,
                  }}
                >
                  <InputLabel
                    shrink
                    sx={{
                      color: colors.GrayDarkMode[700].$value,
                      "&.Mui-focused": {
                        color: colors.Brand[600].$value,
                      },
                    }}
                  >
                    Website (optional)
                  </InputLabel>
                  <Select
                    value={task.website}
                    onChange={(e) =>
                      updateTask(index, "website", e.target.value)
                    }
                    displayEmpty
                    notched
                    label="Website (optional)"
                    size="small"
                    renderValue={(selected) =>
                      selected ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: spacing.Spacing.xs.$value,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: typography.Size.Text.sm.$value,
                              color: colors.GrayLightMode[900].$value,
                            }}
                          >
                            {selected as string}
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: spacing.Spacing.md.$value,
                          }}
                        >
                          <LanguageIcon
                            fontSize="small"
                            sx={{ color: colors.GrayDarkMode[700].$value }}
                          />
                          <Typography
                            sx={{
                              fontSize: typography.Size.Text.sm.$value,
                              color: colors.GrayDarkMode[500].$value,
                            }}
                          >
                            Select Website
                          </Typography>
                        </Box>
                      )
                    }
                    sx={{
                      borderRadius: spacing.Radius.md.$value,
                      fontSize: typography.Size.Text.sm.$value,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.GrayDarkMode[300].$value,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.Brand[600].$value,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.Brand[600].$value,
                        borderWidth: "2px",
                      },
                    }}
                  >
                    {[
                      "Michigan",
                      "California",
                      "New York",
                      "Texas",
                      "Florida",
                    ].map((site) => (
                      <MenuItem key={site} value={site}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: spacing.Spacing.md.$value,
                          }}
                        >
                          {site}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Due Date + Priority + Assignee row — only shown if NOT set via shared props */}
              {(!sharedProps?.dueDate ||
                !sharedProps?.priority ||
                !sharedProps?.assignee) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing.Spacing.md.$value,
                    marginBlock: `${spacing.Spacing["3xl1"].$value} ${spacing.Spacing.xs.$value}`,
                    paddingInline: spacing.Spacing.lg1.$value,
                  }}
                >
                  {/* Due Date — hide if shared */}
                  {!sharedProps?.dueDate && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.Spacing.md.$value,
                        flex: 1,
                      }}
                    >
                      <DatePicker
                        value={task.dueDate}
                        format="DD/MM/YYYY"
                        onChange={(newValue) => {
                          updateTask(index, "dueDate", newValue);
                          setPickerOpen(null);
                        }}
                        open={pickerOpen === index}
                        onClose={() => setPickerOpen(null)}
                        slots={{
                          openPickerIcon: InsertInvitationOutlinedIcon,
                        }}
                        slotProps={{
                          textField: {
                            variant: "standard",
                            size: "small",
                            placeholder: "Due Date",
                            style: { textTransform: "lowercase" },
                            onClick: () => setPickerOpen(index),
                            InputProps: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <InsertInvitationOutlinedIcon
                                    fontSize="medium"
                                    sx={{
                                      color: colors.GrayDarkMode[700].$value,
                                      cursor: "pointer",
                                    }}
                                    onClick={() => setPickerOpen(index)}
                                  />
                                </InputAdornment>
                              ),
                              disableUnderline: true,
                              endAdornment: null,
                            },
                            sx: {
                              flex: 1,
                              "& .MuiInputBase-input": {
                                fontSize: typography.Size.Text.sm.$value,
                                padding:
                                  spacing.Spacing.none.$value + " !important",
                              },
                            },
                          },
                          actionBar: {
                            sx: {
                              "& .MuiButton-root": {
                                color: colors.Brand[700].$value,
                              },
                              "& .MuiButton-root:last-of-type": {
                                color: colors.Brand[600].$value,
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: spacing.Spacing.md.$value,
                    }}
                  >
                    {/* Priority — hide if shared */}
                    {!sharedProps?.priority &&
                      (() => {
                        const priorityConfig: Record<
                          string,
                          { icon: React.ReactNode }
                        > = {
                          high: {
                            icon: (
                              <ExpandLessIcon
                                fontSize="medium"
                                sx={{ color: colors.Error[500].$value }}
                              />
                            ),
                          },
                          medium: {
                            icon: (
                              <DragHandleIcon
                                fontSize="medium"
                                sx={{ color: colors.Warning[500].$value }}
                              />
                            ),
                          },
                          low: {
                            icon: (
                              <ExpandMoreIcon
                                fontSize="medium"
                                sx={{ color: colors.Blue[500].$value }}
                              />
                            ),
                          },
                          highest: {
                            icon: (
                              <KeyboardDoubleArrowUpIcon
                                fontSize="medium"
                                sx={{ color: colors.Error[500].$value }}
                              />
                            ),
                          },
                          lowest: {
                            icon: (
                              <KeyboardDoubleArrowDownIcon
                                fontSize="medium"
                                sx={{ color: colors.Blue[500].$value }}
                              />
                            ),
                          },
                        };

                        return (
                          <Select
                            value={task.priority}
                            onChange={(e) =>
                              updateTask(index, "priority", e.target.value)
                            }
                            displayEmpty
                            variant="standard"
                            size="small"
                            disableUnderline
                            renderValue={(val) => {
                              const config = priorityConfig[val as string];
                              return config ? (
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  {config.icon}
                                </Box>
                              ) : (
                                <FormatListBulletedIcon
                                  fontSize="medium"
                                  sx={{
                                    color: colors.GrayDarkMode[700].$value,
                                  }}
                                />
                              );
                            }}
                            sx={{
                              fontSize: typography.Size.Text.sm.$value,
                              minWidth: "unset",
                              "& .MuiSelect-select": {
                                paddingRight:
                                  spacing.Spacing.md.$value + " !important",
                                paddingBottom:
                                  spacing.Spacing.none.$value + " !important",
                                paddingTop:
                                  spacing.Spacing.none.$value + " !important",
                                display: "flex",
                                alignItems: "center",
                              },
                              "& .MuiSelect-icon": { position: "relative" },
                            }}
                          >
                            {Object.entries({
                              high: {
                                label: "High",
                                icon: (
                                  <ExpandLessIcon
                                    fontSize="medium"
                                    sx={{ color: colors.Error[500].$value }}
                                  />
                                ),
                              },
                              medium: {
                                label: "Medium",
                                icon: (
                                  <DragHandleIcon
                                    fontSize="medium"
                                    sx={{ color: colors.Warning[500].$value }}
                                  />
                                ),
                              },
                              low: {
                                label: "Low",
                                icon: (
                                  <ExpandMoreIcon
                                    fontSize="medium"
                                    sx={{ color: colors.Blue[500].$value }}
                                  />
                                ),
                              },
                              highest: {
                                label: "Highest",
                                icon: (
                                  <KeyboardDoubleArrowUpIcon
                                    fontSize="medium"
                                    sx={{ color: colors.Error[500].$value }}
                                  />
                                ),
                              },
                              lowest: {
                                label: "Lowest",
                                icon: (
                                  <KeyboardDoubleArrowDownIcon
                                    fontSize="medium"
                                    sx={{ color: colors.Blue[500].$value }}
                                  />
                                ),
                              },
                            }).map(([value, { label, icon }]) => (
                              <MenuItem key={value} value={value}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: spacing.Spacing.md.$value,
                                  }}
                                >
                                  {icon}
                                  {label}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        );
                      })()}

                    {/* Assignee — hide if shared */}
                    {!sharedProps?.assignee && (
                      <Select
                        value={task.assignee}
                        onChange={(e) =>
                          updateTask(index, "assignee", e.target.value)
                        }
                        displayEmpty
                        variant="standard"
                        size="small"
                        disableUnderline
                        renderValue={() => (
                          <AccountCircleIcon
                            fontSize="medium"
                            sx={{
                              color: colors.GrayDarkMode[700].$value,
                              display: "flex",
                            }}
                          />
                        )}
                        sx={{
                          fontSize: typography.Size.Text.sm.$value,
                          minWidth: "unset",
                          "& .MuiSelect-select": {
                            paddingRight:
                              spacing.Spacing.md.$value + " !important",
                            paddingBottom:
                              spacing.Spacing.none.$value + " !important",
                            paddingTop:
                              spacing.Spacing.none.$value + " !important",
                            display: "flex",
                            alignItems: "center",
                          },
                          "& .MuiSelect-icon": { position: "relative" },
                        }}
                      >
                        <MenuItem value="">Unassigned</MenuItem>
                      </Select>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          ))}

          {/* Add Another */}
          <Button
            startIcon={<AddIcon />}
            onClick={addAnother}
            sx={{
              color: colors.Brand[600].$value,
              textTransform: "none",
              fontWeight: typography.Font_weight.Medium.$value,
              mb: spacing.Spacing["3xl"].$value,
              width: "100%",
              justifyContent: "center",
            }}
          >
            ADD ANOTHER
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
